/**
 * PATH: packages/email-hub/src/feature/components/StatementViewer.tsx
 * FILE_ID: EMAILHUB-FEATURE-STATEMENTVIEWER-001
 * PURPOSE: Optional injection seam that lets a host app surface a
 *          structured bill/statement affordance inside the email reader
 *          WITHOUT email-hub depending on @quotech/bills.
 *
 * WHY:     The reader (EmailThread + ExpandedContext INTEL chip) lives in
 *          this portable package; the structured bill viewer (BillDataPanel
 *          / BillDrawer) lives in @quotech/bills and is only present in
 *          tenant apps (SAM/Newmark), not in the standalone email.quotech.ai
 *          hub. A context handler keeps the coupling one-directional: the
 *          tenant provides ``openStructuredStatement``; the reader shows
 *          the affordance only when a handler exists.
 *
 * RELATED:
 *   - apps/sam|newmark wrap the email section with
 *     <EmailStatementViewerProvider> and open their BillDrawer.
 *   - EmailThread footer + InvoiceNumberChip (INTEL) consume the seam.
 */
import { createContext, useContext, type ReactNode } from 'react'

/** Categories that may have an extractable bill/statement structure. */
export const STRUCTURED_DOC_CATEGORIES = new Set([
  'bill',
  'supplier',
  'invoice',
  'payment',
])

/** Pure: category slug → whether the structured-bill affordance applies. */
export function isStructuredDocCategory(category: string | null | undefined): boolean {
  return STRUCTURED_DOC_CATEGORIES.has((category || '').toLowerCase())
}

export interface EmailStatementViewerValue {
  /** Open the structured statement/bill reader for an email. The host
   *  resolves emailId -> extracted bill and renders its own drawer/modal
   *  (view-only — never allocates). Undefined when no host wired it. */
  openStructuredStatement?: (emailId: string) => void
}

const EmailStatementViewerContext = createContext<EmailStatementViewerValue>({})

export function EmailStatementViewerProvider({
  value,
  children,
}: {
  value: EmailStatementViewerValue
  children: ReactNode
}) {
  return (
    <EmailStatementViewerContext.Provider value={value}>
      {children}
    </EmailStatementViewerContext.Provider>
  )
}

/** Reader-side hook. Returns ``{}`` (no handler) when no provider is
 *  mounted — safe in the standalone hub. */
export function useEmailStatementViewer(): EmailStatementViewerValue {
  return useContext(EmailStatementViewerContext)
}
